import { Emitter, Event } from "../../../../../features/event-emitter"
import { IdentifierType } from "../../../../system"
import { useCustomerService } from "../../../services/customerService"
import { Data2 } from "../../../../../features/data-types"
import { isDefined } from "../../../../../utils/typeUtils"
import { ModalId, O, useUIActions } from "../../../../../features/ui"
import { useLocation, useNavigate } from "react-router-dom"
import { Customer } from "../../../state/types"
import { useAddressBuilders, useAddressHooks } from "../../../../../features/address"

export const useActions = () => {
    const { canRemoveAddress } = useAddressHooks()
    const { state } = useLocation()
    const navigate = useNavigate()
    
    const { buildAddress } = useAddressBuilders()

    const { openModal, closeModal } = useUIActions()
    const { createCustomer, updateCustomer } = useCustomerService()

    const removeAddress = async (addressId: string, addressesData: Data2[]): Promise<O<Data2[]>> => {
        Emitter.emit(Event.DB_REMOVE)

        let newAddressesData: O<Data2[]>
        const canRemoveCustomerAddress = await canRemoveAddress(addressId)
        if (canRemoveCustomerAddress) {
            newAddressesData = addressesData.filter(addressData => addressData.id !== addressId)
            Emitter.emit(Event.DB_REMOVE_DONE, false)
        } else {
            closeModal(ModalId.SPINNER)
            setTimeout(() => openModal(ModalId.ARCHIVE), 500)
        }
        
        return newAddressesData
    }

    const archiveAddress = async (addressId: string, addressesData: Data2[]) => {
        Emitter.emit(Event.DB_ARCHIVE)
        const newAddressesData = addressesData.map(addressData => addressData.id === addressId ?
            buildAddress({
                ...addressData,
                archived: true
            }) :
            addressData
        )
        Emitter.emit(Event.DB_ARCHIVE_DONE)
        
        return newAddressesData
    }

    const submitCustomerOrOpenModal = async (
        data: Data2,
        identifierType: IdentifierType,
        identifier: number,
        emailsData: Data2[],
        phonesData: Data2[],
        mainAddressData?: Data2,
        deliveryAddressesData?: Data2[],
        contactData?: Data2,
        creditLimit?: number,
        creditDays?: number,
        createMode?: boolean
    ) => {
        if (emailsData.length === 0 && phonesData.length === 0) {
            return openModal(ModalId.CUSTOMER_WARNING)
        } else {
            submitCustomer(
                data,
                identifierType,
                identifier,
                emailsData,
                phonesData,
                mainAddressData,
                deliveryAddressesData,
                contactData,
                creditLimit,
                creditDays,
                createMode
            )
        }
    }

    const submitCustomer = async (
        data: Data2,
        identifierType: IdentifierType,
        identifier: number,
        emailsData: Data2[],
        phonesData: Data2[],
        mainAddressData?: Data2,
        deliveryAddressesData?: Data2[],
        contactData?: Data2,
        creditLimit?: number,
        creditDays?: number,
        createMode?: boolean
    ) => {
        Emitter.emit(Event.DB_SUBMIT)

        const { id, name, category, customerTypeId, vatCondition } = data
        const { contactName, contactPhone } = contactData || {}
        
        const customerData = {
            id,
            name,
            identifierType,
            identifier,
            category,
            customerTypeId,
            emails: emailsData.map(data => data.email! as string),
            phones: phonesData.map(data => data.phone! as number),
            vatCondition,
            contactName,
            contactPhone
        }
        const creditAccountData = isDefined(creditLimit) && isDefined(creditDays) ? {
            creditLimit,
            creditDays
        } : undefined

        let savedCustomer: Customer
        if (createMode) {
            savedCustomer = await createCustomer(customerData, mainAddressData, deliveryAddressesData, creditAccountData)
        } else {
            savedCustomer = await updateCustomer(customerData, mainAddressData, deliveryAddressesData, creditAccountData)
        }

        if (state?.from) {
            setTimeout(() => {
                closeModal(ModalId.SPINNER)
                const options = {
                    state: {
                        ...state,
                        customerId: savedCustomer.id
                    }
                }
                navigate(state.from, options)
            }, 1000)
        } else {
            Emitter.emit(Event.DB_SUBMIT_DONE, createMode)
        }
    }
    
    return {
        removeAddress,
        archiveAddress,
        submitCustomerOrOpenModal,
        submitCustomer
    }
}
